import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { DeleteOutlined } from '@vindr/icons';
import { useState } from 'react';

export interface AnnotationItem {
  uid: string;
  label: string;
  isActive: boolean;
  displayText: string[];
  // onClick?: () => void;
}

const PresentationStateItem = ({
  label,
  items,
  currentUser,
  isActive = false,
  activeItem,
  createdAt,
  className,
  showProb = true,
  onClickItem = null,
  onDeleteGroup = null,
  onDeleteItem = null,
}: {
  label: string;
  currentUser: any;
  isActive: boolean;
  activeItem: {
    annotationUID: string;
  };
  createdAt?: string;
  items: AnnotationItem[];
  className?: string;
  showProb?: boolean;
  onClickItem?: (item: AnnotationItem) => void;
  onDeleteItem?: (item: AnnotationItem) => void;
  onDeleteGroup?: (items: AnnotationItem[]) => void;
}): JSX.Element => {
  const { t } = useTranslation('ViewerTool');
  const [isExpand, setIsExpand] = useState(true);

  const [isHovering, setIsHovering] = useState(false);

  const onMouseEnter = () => setIsHovering(true);
  const onMouseLeave = () => setIsHovering(false);

  return (
    <div
      className={classnames(className, 'overflow-hidden text-lg mb-2')}
    >
      <div
        className={`p-1 relative rounded-sm bg-background-dark-7 cursor-pointer ${isActive ? 'bg-primary-active' : ''}`}
        onClick={() => setIsExpand(!isExpand)}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <div>{label}</div>
        <DeleteOutlined
          className={classnames(
            'text-white text-2xl w-6 absolute cursor-pointer transition duration-300',
            { 'invisible opacity-0 mr-2': !isActive && !isHovering },
            { 'visible opacity-1': !isActive && isHovering }
          )}
          name="delete-group"
          style={{
            top: 8,
            right: 4,
            transform: isActive || isHovering ? '' : 'translateX(100%)',
          }}
          onClick={(event) => {
            event.stopPropagation();
            onDeleteGroup(items)
          }}
        />
      </div>
      {isExpand && (
        <AnnotationList
          currentUser={currentUser}
          items={items}
          onClickItem={onClickItem}
          onDeleteItem={onDeleteItem}
          activeItem={activeItem}
        />
      )}
    </div>
  );
};

// const StateLabel = ({fullname, username, createdAt, isActive, onClick}) => {
//   return (
//     <div className={`p-1 h-[60px] bg-background-dark-7 cursor-pointer ${isActive ? 'bg-primary-active' : ''}`} onClick={onClick}>
//       <div className="text-bold">{fullname} ({username})</div>
//       <div>{createdAt}</div>
//     </div>
//   )
// }

const AnnotationList = ({ items, onClickItem, currentUser, onDeleteItem, activeItem }) => {
  return (
    <div className="mt-1">
      {items.map(({label, displayText, uid, createdAt, user}, index) => {
        return (
          <AnnotationItem
            key={uid}
            uid={uid}
            currentUser={currentUser}
            user={user || currentUser}
            createdAt={createdAt}
            index={index + 1}
            label={label}
            displayText={displayText}
            onClick={onClickItem}
            isActive={activeItem.annotationUID === uid}
            onDelete={onDeleteItem}
          />
        )
      })}
    </div>
  )
}

const AnnotationItem = ({
  uid,
  user,
  currentUser,
  index,
  label,
  createdAt,
  displayText,
  isActive,
  onClick,
  onDelete,
  // onEdit,
}) => {
  const [isHovering, setIsHovering] = useState(false);
  const [isDeleteHovering, setIsDeleteHovering] = useState(false);

  // const onEditHandler = event => {
  //   event.stopPropagation();
  //   onEdit({ uid, isActive, event });
  // };

  const onClickHandler = event => onClick({ uid, isActive, event });

  const onMouseEnter = () => setIsHovering(true);
  const onMouseLeave = () => setIsHovering(false);

  const onDeleteMouseEnter = () => setIsDeleteHovering(true);
  const onDeleteMouseLeave = () => setIsDeleteHovering(false);

  return (
    <div
      className={classnames(
        'group flex cursor-pointer rounded-sm border outline-none transition duration-300 mb-1',
        {
          'rounded overflow-hidden border-primary-light': isActive || isDeleteHovering,
          'border-transparent': !(isActive || isDeleteHovering),
        }
      )}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={onClickHandler}
      role="button"
      data-cy={'measurement-item'}
    >
      <div
        className={classnames(
          'text-center w-6 py-1 text-base transition duration-300',
          {
            'bg-primary-light text-black active border border-primary-light': isActive,
            'bg-primary-dark text-primary-light group-hover:bg-secondary-main': !isActive,
          }
        )}
      >
        {index}
      </div>
      <div className="relative flex flex-col flex-1 px-2 py-1">
        <span className="mb-1 text-base text-primary-light">{label}</span>
        {displayText.map((line, i) => (
          <span
            key={i}
            className="pl-2 text-base text-white border-l border-primary-light"
            dangerouslySetInnerHTML={{ __html: line }}
          ></span>
        ))}
        <span
          className="pl-2 text-base text-white border-l border-primary-light text-opacity-5"
          dangerouslySetInnerHTML={{ __html: `${user.fullname || ''}` }}
        ></span>
        {createdAt && <span
          className="pl-2 text-base text-white border-l border-primary-light text-opacity-5"
          dangerouslySetInnerHTML={{ __html: createdAt }}
        ></span>}
        {(currentUser?.is_super || currentUser?.id === user.id) && (
          <DeleteOutlined
            className={classnames(
              'text-white text-2xl w-6 absolute cursor-pointer transition duration-300',
              { 'invisible opacity-0 mr-2': !isActive && !isHovering },
              { 'visible opacity-1': !isActive && isHovering }
            )}
            name="delete-item"
            onMouseEnter={onDeleteMouseEnter}
            onMouseLeave={onDeleteMouseLeave}
            style={{
              top: 4,
              right: 4,
              transform: isActive || isHovering ? '' : 'translateX(100%)',
            }}
            onClick={event => onDelete({ uid, event })}
          />
        )}
      </div>
    </div>
  );
};

export default PresentationStateItem;
